import styled, { css, keyframes } from 'styled-components'
import { StyledLoadingProps } from './Loading.types'

// Animation keyframes
const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
`

const bounce = keyframes`
  0%, 80%, 100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
`

const wave = keyframes`
  0%, 60%, 100% {
    transform: initial;
  }
  30% {
    transform: translateY(-15px);
  }
`

// Size configurations
const sizeConfig = {
  small: {
    spinner: '16px',
    dot: '4px',
    bar: { width: '2px', height: '12px' },
    text: '12px',
  },
  medium: {
    spinner: '24px',
    dot: '6px',
    bar: { width: '3px', height: '18px' },
    text: '14px',
  },
  large: {
    spinner: '32px',
    dot: '8px',
    bar: { width: '4px', height: '24px' },
    text: '16px',
  },
}

// Speed configurations
const speedConfig = {
  slow: '2s',
  normal: '1s',
  fast: '0.5s',
}

export const LoadingContainer = styled.div<StyledLoadingProps>`
  display: ${(props) => (props.$center ? 'flex' : 'inline-flex')};
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};

  ${(props) =>
    props.$center &&
    css`
      justify-content: center;
      width: 100%;
      min-height: 60px;
    `}
`

export const SpinnerWrapper = styled.div<StyledLoadingProps>`
  width: ${(props) => sizeConfig[props.$size].spinner};
  height: ${(props) => sizeConfig[props.$size].spinner};
  border: 2px solid ${({ theme }) => theme.colors.border.default};
  border-top: 2px solid ${(props) => props.$color || props.theme.colors.primary};
  border-radius: 50%;
  animation: ${spin} ${(props) => speedConfig[props.$speed]} linear infinite;
`

export const DotsWrapper = styled.div<StyledLoadingProps>`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xsmall};
`

export const Dot = styled.div<StyledLoadingProps & { $delay: number }>`
  width: ${(props) => sizeConfig[props.$size].dot};
  height: ${(props) => sizeConfig[props.$size].dot};
  background-color: ${(props) => props.$color || props.theme.colors.primary};
  border-radius: 50%;
  animation: ${bounce} ${(props) => speedConfig[props.$speed]} infinite;
  animation-delay: ${(props) => props.$delay * 0.1}s;
`

export const PulseWrapper = styled.div<StyledLoadingProps>`
  width: ${(props) => sizeConfig[props.$size].spinner};
  height: ${(props) => sizeConfig[props.$size].spinner};
  background-color: ${(props) => props.$color || props.theme.colors.primary};
  border-radius: 50%;
  animation: ${pulse} ${(props) => speedConfig[props.$speed]} ease-in-out
    infinite;
`

export const BarsWrapper = styled.div<StyledLoadingProps>`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xsmall};
  align-items: end;
`

export const Bar = styled.div<StyledLoadingProps & { $delay: number }>`
  width: ${(props) => sizeConfig[props.$size].bar.width};
  height: ${(props) => sizeConfig[props.$size].bar.height};
  background-color: ${(props) => props.$color || props.theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  animation: ${wave} ${(props) => speedConfig[props.$speed]} ease-in-out
    infinite;
  animation-delay: ${(props) => props.$delay * 0.1}s;
`

export const LoadingText = styled.div<StyledLoadingProps>`
  font-size: ${(props) => sizeConfig[props.$size].text};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  text-align: center;
`
